export const mv = {
  meta: { title: 'AI MV 生成', desc: '从一个想法生成歌词、音乐和 MV。' },
  hero: {
    kicker: 'AI 音乐视频工具',
    title: '一句话生成歌词、音乐和 MV',
    desc: '面向新手的引导式流程：描述歌曲，编辑歌词，生成音乐，再上传图片制作 MV 草稿。',
    tags: ['一句话提示词', '可编辑歌词', '音乐预览', '歌词到 MV'],
    cardTitle: '可编辑副歌',
    cardText: '每一行歌词都可以先确认，再进入音乐和 MV 生成。',
  },
  steps: { lyrics: '歌词', music: '音乐', video: 'MV' },
  flow: {
    available: '可进入',
    current: '当前步骤',
    done: '已完成',
    locked: '待解锁',
    stepOf: '第 {current} / {total} 步',
    lyrics: {
      title: '先创作歌词',
      desc: '从一句话开始生成结构化歌词，后续音乐和 MV 都会基于这版歌词继续创作。',
    },
    music: {
      title: '用确认后的歌词生成音乐',
      desc: '根据歌词继续设置音乐风格、人声、节奏和音乐提示词。',
    },
    video: {
      title: '音乐完成后制作 MV',
      desc: '上传图片并描述画面方向，把歌曲继续生成可预览的 MV 草稿。',
    },
  },
  summary: {
    lyricsTitle: '已确认歌词',
    musicTitle: '已生成音乐',
    editLyrics: '编辑歌词',
    editMusic: '编辑音乐',
  },
  generator: {
    lyrics: {
      idea: '描述你想做的歌',
      placeholder: '例如：写一首关于夏天毕业旅行的治愈中文歌',
      genre: '音乐风格',
      mood: '心情',
      vocal: '人声',
      language: '语言',
      length: '长度',
      settings: '歌词设置',
      button: '生成歌词',
    },
    music: {
      prompt: '描述你想要的音乐',
      placeholder: '例如：明亮流行风，副歌抓耳，吉他清爽，温柔女声',
      upload: '上传音乐参考图片',
      formats: '可选。上传 1-5 张图片，根据画面氛围生成音乐。',
      selected: '已选择 {count} 张图片',
      style: '音乐风格',
      mood: '情绪',
      vocal: '人声',
      language: '语言',
      mode: '模式',
      duration: '时长',
      settings: '音乐设置',
      button: '生成音乐',
    },
    video: {
      prompt: '描述你想要的 MV',
      placeholder: '例如：夏日毕业旅行感，柔和转场，温暖怀旧色调',
      upload: '上传 MV 图片',
      formats: '支持 PNG、JPG、WebP，可上传单图或多图。',
      selected: '已选择 {count} 张图片',
      ratio: '画幅',
      resolution: '分辨率',
      visual: '画面风格',
      settings: 'MV 设置',
      button: '生成 MV',
    },
  },
  preview: {
    emptyLyricsTitle: '还没有歌词',
    emptyLyrics: '生成的歌词会显示在这里。',
    emptyLyricsHint: '输入歌曲想法后，可生成标准结构的可编辑歌词',
    emptyMusicTitle: '还没有音乐',
    emptyMusic: '歌词准备好后，可以生成音乐。',
    emptyMusicHint: '支持音乐风格、人声、节奏和自定义音乐提示词',
    emptyMvTitle: '还没有 MV',
    emptyMv: '上传图片后，可以生成最终 MV 草稿。',
    emptyMvHint: '支持单图、多图、画幅比例和画面风格控制',
    lyricsTitle: '可编辑歌词',
    lyricsHint: '可以复制、编辑、插入或删除歌词行。',
    lyricsReady: '歌词已准备好',
    lyricsNext: '确认这版歌词后，再用它继续生成音乐。',
    musicTitle: '音乐预览',
    musicPlaceholder: '这里会显示音乐试听。',
    musicReady: '音乐已生成',
    musicNext: '确认这段音乐后，再用它继续制作 MV。',
    mvTitle: 'MV 预览',
    mvPlaceholder: '这里会显示视频预览。',
    sourceImages: '使用的图片',
    lyricsBackdrop: '同步歌词',
  },
  actions: {
    copy: '复制歌词',
    copySuccess: '歌词已复制',
    copyFailed: '复制失败',
    edit: '编辑歌词',
    save: '保存歌词',
    download: '下载',
    downloadFailed: '下载失败',
    play: '播放',
    pause: '暂停',
    volume: '音量',
    audioLoading: '正在加载音频...',
    audioLoadFailed: '音频加载失败',
    regenerate: '重新生成',
    regenerateWithCredits: '重新生成（{count} 积分）',
    generateWithCredits: '{label}（{count} 积分）',
    generatingWithCredits: '生成中 {progress}%（{count} 积分）',
    expand: '展开',
    collapse: '收起',
    selectSettings: '请选择设置',
    nextMusic: '使用这版歌词并继续',
    nextMv: '使用这段音乐并继续',
  },
  examples: {
    title: 'AI MV 示例：歌曲故事、纪念场景与品牌短片',
    sub: '看看一句想法如何被拆解成结构化歌词、音乐方向和基于图片的 MV 草稿。',
    items: [
      { badge: '旅行', title: '毕业旅行', text: '治愈歌词、流行旋律和照片回忆 MV。' },
      { badge: '纪念', title: '周年纪念歌', text: '浪漫歌词、温柔人声和电影感转场。' },
      { badge: '品牌', title: '产品主题曲', text: '抓耳副歌和品牌视觉，适合短视频推广。' },
    ],
  },
  howto: {
    title: '如何用 AI 创建完整音乐 MV',
    sub: '通过三个引导步骤完成创作：生成可编辑歌词，确定音乐风格，再上传图片制作精致 MV 草稿。',
    items: [
      { title: '生成歌词', text: '输入一句话，选择心情和语言，得到结构化歌词。' },
      { title: '生成音乐', text: '选择音乐风格、人声和节奏，生成后默认使用。' },
      { title: '制作 MV', text: '上传单图或多图，设置尺寸和风格，生成 MV 草稿。' },
    ],
  },
  ask: {
    title: 'AI MV 生成器 - 常见问题',
    sub: '了解歌词编辑、音乐生成、图片上传、重新生成和 MV 下载相关问题。',
    items: [
      { q: '小白能用吗？', a: '可以。一句话就能开始，每一步都有明确的下一步按钮。' },
      { q: '歌词可以改吗？', a: '可以复制、编辑、插入和删除歌词行。' },
      { q: '不满意可以重来吗？', a: '可以，每一步都支持重新生成，同时保留整体流程。' },
    ],
  },
};
